<template>
  <v-card v-if="ready && (!profile || !profile.id)">
    <v-card-title :class="themes.candidate.light.dialogTitle" min-width="50vw">
      <span class="headline">Signup Incomplete</span>
    </v-card-title>

    <v-card-text>
      <p>It looks like you still need to complete the signup steps.</p>
      <p>Please click the button below when you are ready to complete your profile.</p>
    </v-card-text>

    <v-card-actions>
      <v-spacer />
      <v-btn :color="themes.candidate.light.primaryActionButton" @click="$emit('signup')">
        <v-icon left>mdi-account</v-icon>
        Complete Profile
      </v-btn>
    </v-card-actions>
  </v-card>

  <v-card class="candidate profile wizard" v-else>
    <v-card-title>
      <v-layout row wrap>
        <v-flex xs12>
          <div class="text-xs-right">
            <v-tooltip left>
              <v-btn
                slot="activator"
                :color="themes.candidate.light.primaryActionButton"
                @click="$emit('preview')"
                dark
                fab
              >
                <v-icon>mdi-eye</v-icon>
              </v-btn>
              <span>View Profile</span>
            </v-tooltip>
          </div>
        </v-flex>
        <v-flex xs12>
          <div class="headline text-xs-center">Edit Profile</div>
        </v-flex>
        <v-flex xs12 md10 offset-md1 lg8 offset-lg2 class="py-4" v-if="profile.completion">
          <v-layout row>
            <div class="pl-2">Progress...</div>
            <v-spacer />
            <div class="pr-2 font-weight-bold">{{ Math.round(profile.completion, 0) }} %</div>
          </v-layout>
          <v-progress-linear
            :color="themes.candidate.light.success"
            :value="profile.completion"
            :height="24"
            style="margin: 0; border-radius: 1rem;"
          />
        </v-flex>
      </v-layout>
    </v-card-title>

    <v-tabs
      v-model="selected"
      :color="themes.candidate.light.secondaryActionButton"
      :height="$vuetify.breakpoint.mdAndUp ? 130 : 90"
      ref="tabs"
      icons-and-text
      show-arrows
      hide-slider
      centered
    >
      <v-tab
        v-for="(tab, i) in tabs"
        :key="`tab-${i}`"
        :active-class="`${themes.candidate.light.primaryActionButton} v-tabs__item--active`"
      >
        <ProfileWizardTab
          :actionItems="actionItems"
          :active="selected === i"
          :profile="profile"
          :tab="tab"
        />
      </v-tab>
    </v-tabs>

    <v-layout row justify-center class="pt-2">
      <div class="text-xs-center">
        <v-tabs v-model="selected" slider-color="transparent" centered>
          <v-tab
            v-for="(tab, i) in tabs"
            :key="`tab-icon-${i}`"
            :color="themes.candidate.light.secondaryActionButton"
          >
            <v-icon
              :color="
                i === selected ? themes.candidate.light.primary : 'themes.candidate.light.secondary'
              "
              class="px-2"
              small
            >
              mdi-circle
            </v-icon>
          </v-tab>
        </v-tabs>
      </div>
    </v-layout>

    <v-card-text style="min-height: 40vh;">
      <v-tabs-items v-if="ready" v-model="selected">
        <v-tab-item v-for="(tab, i) in tabs" :key="`tab-item-${i}`">
          <v-alert error transition="scale-transition" :value="!!errors" class="ma-0">
            <ul>
              <li v-for="(message, i) in errors" :key="`error-${i}`">
                {{ i | capitalize({ onlyFirstLetter: true }) }} - {{ message.join(', ') }}
              </li>
            </ul>
          </v-alert>
          <article v-if="selected == i || accessed.find(ix => i === ix)"
            :is="`${tab.text.replace(' ', '')}Section`"
            :profile="profile"
            @refresh="refresh"
            :style="!!errors ? `border: solid 2px ${$vuetify.theme.error}` : ''"
          />
        </v-tab-item>
      </v-tabs-items>
      <v-progress-linear
        v-else
        class="ma-0"
        :color="themes.candidate.light.loading"
        :height="5"
        indeterminate
      />
    </v-card-text>
  </v-card>
</template>

<script>
// Components
import AboutMeSection from '@/components/candidate/profile/edit/about/AboutMeSection'
import EducationSection from '@/components/candidate/profile/edit/qualifications/EducationSection'
import ExperienceSection from '@/components/candidate/profile/edit/experience/ExperienceSection'
import MyDetailsSection from '@/components/candidate/profile/edit/details/MyDetailsSection'
import MyDocumentsSection from '@/components/candidate/profile/edit/MyDocumentsSection'
import ProfileWizardTab from '@/components/candidate/profile/edit/ProfileWizardTab'

// Data
const tabs = [
  { text: 'About Me', hash: '#me', icon: 'account' },
  { text: 'My Details', hash: '#details', icon: 'pencil' },
  { text: 'Education', hash: '#education', icon: 'school' },
  { text: 'Experience', hash: '#experience', icon: 'briefcase' },
  { text: 'My Documents', hash: '#documents', icon: 'file-multiple' },
]

// Helpers
import { mapGetters } from 'vuex'
import _ from 'lodash'

export default {
  components: {
    AboutMeSection,
    EducationSection,
    ExperienceSection,
    MyDetailsSection,
    MyDocumentsSection,
    ProfileWizardTab,
  },

  created() {
    this.refresh()
    if (this.$route.hash) this.setSelectedFromHash(this.$route.hash)
  },

  data() {
    return {
      ready: false,
      selected: 0,
      accessed: [0],
      tabs,
    }
  },

  computed: {
    ...mapGetters({
      actionItems: 'candidate/actionItems/all',
      profile: 'candidate/profile/model',
    }),

    errors() {
      if (!_.get(this, 'profile.errors[0]') || !Object.keys(this.profile.errors).length) {
        return
      }

      return this.profile.errors
    },
  },

  watch: {
    '$route.hash': {
      handler(hash) {
        this.setSelectedFromHash(hash)
      },
    },
  },

  methods: {
    refresh() {
      Promise.all([
        this.$store.dispatch('candidate/actionItems/fetchAll'),
        this.$store.dispatch('candidate/profile/fetch'),
      ])
        .then(() => {})
        .catch(() => {})
        .finally(() => (this.ready = true))
    },

    setSelectedFromHash(hash) {
      const hashSelected = this.tabs.findIndex(t => t.hash === hash)
      this.accessed.push(hashSelected)
      this.selected = hashSelected >= 0 ? hashSelected : 0
    },
  },
}
</script>

<style lang="scss">
.candidate.profile.wizard {
  .v-tabs__wrapper {
    overflow-x: scroll;
    overflow: -moz-scrollbars-none;
    o-ms-overflow-style: none;
    scroll-behavior: smooth;

    &::-webkit-scrollbar {
      display: none !important;
    }
  }

  a.v-tabs__item {
    background-color: rgba(0, 0, 0, 0);
    padding: 0 0 0 0;
    border: 0px;
  }
}
</style>
